import React, { useState } from 'react';
import { TruckIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '../common/Tooltip';
import { Order } from '../../types/order';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { DocumentChainService } from '../../services/documentChainService';
import toast from 'react-hot-toast';

interface OrderToDeliveryNoteButtonProps {
  order: Order;
  onDeliveryNoteCreated?: (deliveryNoteId: string) => void;
  showTooltip?: boolean;
  className?: string;
  disabled?: boolean;
}

const OrderToDeliveryNoteButton: React.FC<OrderToDeliveryNoteButtonProps> = ({ 
  order,
  onDeliveryNoteCreated,
  showTooltip = true,
  className = "text-gray-400 hover:text-blue-400",
  disabled = false
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);

  const handleCreateDeliveryNote = async () => {
    if (!user?.organizationId || creating || disabled) return;

    try {
      setCreating(true);
      
      const deliveryNote = await DocumentChainService.createDeliveryNoteFromOrder(
        order,
        user.organizationId,
        user.uid
      );

      toast.success('Szállítólevél sikeresen létrehozva!');
      
      if (onDeliveryNoteCreated) {
        onDeliveryNoteCreated(deliveryNote.id);
      }
      
    } catch (error) {
      console.error('Error creating delivery note:', error);
      toast.error('Hiba történt a szállítólevél létrehozásakor');
    } finally {
      setCreating(false);
    }
  };

  const buttonContent = (
    <button
      onClick={handleCreateDeliveryNote}
      disabled={creating || disabled}
      className={`${className} flex items-center transition-colors ${
        creating || disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      aria-label="Szállítólevél készítése"
    >
      <TruckIcon className={`h-4 w-4 mr-1 ${creating ? 'animate-pulse' : ''}`} />
      <span className="text-xs font-medium">
        {creating ? 'Készítés...' : 'Szállítólevél'}
      </span>
    </button>
  );

  if (showTooltip && !disabled) {
    return (
      <Tooltip content="Szállítólevél készítése a megrendelésből">
        {buttonContent}
      </Tooltip>
    );
  }

  return buttonContent;
};

export default OrderToDeliveryNoteButton; 